import { useContext, useState, useEffect } from 'react';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import * as z from 'zod';

import config from '@/config';
import { Issue, USER_ROLE } from '@/constants';
import { readJsonFromStorage } from '@shared/storage';

import { HomeContext } from '../home.context';
import {
	AUTH_KEY,
	AuthenticateUserResponse,
} from '@shared/auth/authentication.hooks';

// This hook is only ever rendered on the home route (`magazines.tsx`, from
// poets' `home/home.tsx`), so unlike `useAuth` it can safely bind to one
// route id instead of reading `strict: false`.
const routeApi = getRouteApi('/');

const AuthenticateUserResponseSchema = z.object({
	uid: z.string().optional(),
	expirationDate: z.string().optional(),
	email: z.string().optional(),
	subscriptionLevel: z.string().optional(),
	digitalAccess: z.boolean(),
	subscriptionStatus: z.string().optional(),
	features: z.array(z.string()),
}) satisfies z.ZodType<AuthenticateUserResponse>;

export function useIsSupportMagazine(): null | boolean {
	
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
		const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	// Seeded with whatever can be known synchronously, rather than `null`
	// until the effect below runs: the reader's Back view transition
	// snapshots this page's first render, and with `null` every cover
	// rendered as the no-access subscription link, which carries no
	// `view-transition-name` -- nothing for the cover morph to land on. The
	// effect still runs and settles the cases this can't (token/cognito's
	// delayed check, the missing-`user` redirect).
	const [isSupported, setIsSupported] = useState<null | boolean>(() =>
		initialIsSupported(!!isAuthenticated, isAdmin),
	);

	useEffect(() => {
		if (!config.app.auth.enabled) {
			setIsSupported(true);
			return;
		}

		if (
			config.app.auth.type === 'token' ||
			config.app.auth.type === 'cognito'
		) {
			setTimeout(() => {
				if (isAuthenticated) {
					setIsSupported(true);
				} else {
					setIsSupported(false);
				}
			}, 1000);
			return;
		}

		if(isAdmin) {
			setIsSupported(true);
			return;
		}

		if (!isAuthenticated) {
			return;
		}
		if (localStorage.getItem('user') === null) {
			window.location.href = '/';
			return;
		}
		const user = readJsonFromStorage('user', AuthenticateUserResponseSchema, {
			digitalAccess: false,
			features: [],
		});
		setIsSupported(user.digitalAccess);
	}, [isAdmin, isAuthenticated]);
	return isSupported;
}

function initialIsSupported(
	isAuthenticated: boolean,
	isAdmin: boolean,
): null | boolean {
	if (!config.app.auth.enabled) {
		return true;
	}
	if (
		config.app.auth.type === 'token' ||
		config.app.auth.type === 'cognito'
	) {
		return null;
	}
	if (isAdmin) {
		return true;
	}
	if (!isAuthenticated || localStorage.getItem('user') === null) {
		return null;
	}
	return readJsonFromStorage('user', AuthenticateUserResponseSchema, {
		digitalAccess: false,
		features: [],
	}).digitalAccess;
}

export function useSelectIssue(issues: Array<Issue>) {
	const navigate = useNavigate();
	const search = routeApi.useSearch();
	const { setSelectedIssue } = useContext(HomeContext);
	useEffect(() => {
		if (!issues.length) {
			return;
		}
		const section = 'magazines';

		const id = search.id;
		if (!id) {
			navigate({
				to: '/',
				search: (prev: Record<string, unknown>) => ({
					...prev,
					section,
				}),
			});
			return;
		}

		const filteredIssue = issues
			.filter(issue => `${issue.id}` === `${id}`)
			.pop();

		if (filteredIssue) {
			setSelectedIssue(filteredIssue);
			navigate({
				to: '/',
				search: (prev: Record<string, unknown>) => ({
					...prev,
					id: filteredIssue.id,
				}),
			});
			return;
		}

		setSelectedIssue(null);
		navigate({
			to: '/',
			search: (prev: Record<string, unknown>) => ({
				...prev,
				id: '',
			}),
		});
	}, [search, issues, setSelectedIssue, navigate]);
}

